import type { ChatMessageView, ErrorMsg } from '@shared/protocol/messages';
import { CHAT_LIMITS } from '@shared/protocol/messages';
import { Emitter } from '../game/core/Emitter';
import type { MultiplayerClient } from './MultiplayerClient';

type ChatEvents = { change: readonly ChatMessageView[]; error: ErrorMsg };

/**
 * Client-side mirror of the world chat (13+). It only shows what the server
 * sent, already filtered; after a reconnect it joins again by itself.
 */
export class ChatSession extends Emitter<ChatEvents> {
  messages: ChatMessageView[] = [];
  /** The player wants to see the chat (a chat panel is open). */
  private wanted = false;

  constructor(private readonly mp: MultiplayerClient) {
    super();
    mp.on('chat.history', (m) => {
      this.messages = m.messages;
      this.emit('change', this.messages);
    });
    mp.on('chat.message', (m) => {
      this.messages = [...this.messages, m.message].slice(-CHAT_LIMITS.history);
      this.emit('change', this.messages);
    });
    mp.on('chat.deleted', (m) => {
      this.messages = this.messages.filter((x) => x.id !== m.id);
      this.emit('change', this.messages);
    });
    mp.on('welcome', () => {
      if (this.wanted) this.join();
    });
  }

  /** Start receiving (now, or as soon as the socket is online); the server answers with the latest messages. */
  open(): void {
    this.wanted = true;
    if (this.mp.state === 'online') this.join();
  }

  close(): void {
    this.wanted = false;
    if (this.mp.state === 'online') this.mp.send({ t: 'chat.leave' }).catch(() => {});
  }

  private join(): void {
    this.mp.send({ t: 'chat.join' }).catch((e: ErrorMsg) => this.emit('error', e));
  }

  send(text: string): Promise<void> {
    return this.mp.send({ t: 'chat.send', text });
  }

  report(messageId: string): Promise<void> {
    return this.mp.send({ t: 'chat.report', messageId });
  }
}
